import React, { useEffect, useState } from 'react';
import { UploadCloudIcon, FolderIcon, ImageIcon, SearchIcon, FilterIcon, TrashIcon, CopyIcon, EyeIcon, XIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
import { useSiteContent } from '../../contexts/SiteContentContext';
const Toast = ({
  message,
  type,
  onClose
}: {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);
  return <div className={`fixed top-20 right-8 z-50 flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-white animate-fadeIn ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`} data-id="element-2165">
      {type === 'success' ? <CheckCircleIcon size={20} data-id="element-2166" /> : <AlertCircleIcon size={20} data-id="element-2167" />}
      <span className="font-medium text-sm" data-id="element-2168">{message}</span>
      <button onClick={onClose} className="ml-4 hover:opacity-80" data-id="element-2169">
        <XIcon size={16} data-id="element-2170" />
      </button>
    </div>;
};
const MediaManager = () => {
  const {
    mediaItems,
    addMediaItem,
    deleteMediaItem
  } = useSiteContent();
  const [activeFolder, setActiveFolder] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  // Modals
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<any>(null);
  // Upload form state
  const [uploadUrl, setUploadUrl] = useState('');
  const [uploadName, setUploadName] = useState('');
  const [uploadFolder, setUploadFolder] = useState('general');
  const folders = [{
    id: 'all',
    name: 'All Media'
  }, {
    id: 'general',
    name: 'General'
  }, {
    id: 'campus',
    name: 'Campus & Facilities'
  }, {
    id: 'events',
    name: 'Events'
  }, {
    id: 'anniversary',
    name: '70th Anniversary'
  }, {
    id: 'alumni',
    name: 'Alumni'
  }];
  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({
      message,
      type
    });
  };
  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    showToast('URL copied to clipboard!');
  };
  const handleDelete = () => {
    if (currentItem) {
      deleteMediaItem(currentItem.id);
      showToast('File deleted from media library');
      setIsDeleteModalOpen(false);
      setCurrentItem(null);
    }
  };
  const handleUploadSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadUrl || !uploadName) {
      showToast('Please provide both URL and Name', 'error');
      return;
    }
    const newItem = {
      id: Date.now(),
      url: uploadUrl,
      name: uploadName,
      size: 'Unknown',
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      folder: uploadFolder
    };
    addMediaItem(newItem);
    showToast('File added to media library');
    setIsUploadModalOpen(false);
    setUploadUrl('');
    setUploadName('');
  };
  // Calculate folder counts dynamically
  const getFolderCount = (folderId: string) => {
    if (folderId === 'all') return mediaItems.length;
    return mediaItems.filter(item => item.folder === folderId).length;
  };
  // Filter items
  const filteredItems = mediaItems.filter(item => {
    const matchesFolder = activeFolder === 'all' || item.folder === activeFolder;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFolder && matchesSearch;
  });
  return <div className="space-y-6 animate-fadeIn h-[calc(100vh-8rem)] flex flex-col relative" data-id="element-2171">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} data-id="element-2172" />}

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 flex-shrink-0" data-id="element-2173">
        <div data-id="element-2174">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2175">Media Library</h1>
          <p className="text-sm text-gray-500" data-id="element-2176">
            Manage images, documents, and other files.
          </p>
        </div>
        <button onClick={() => setIsUploadModalOpen(true)} className="inline-flex items-center px-4 py-2 bg-blue-950 text-yellow-400 rounded-lg text-sm font-medium hover:bg-blue-900 shadow-sm transition-colors" data-id="element-2177">
          <UploadCloudIcon size={16} className="mr-2" data-id="element-2178" />
          Upload Files
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-6 flex-1 min-h-0" data-id="element-2179">
        {/* Sidebar Folders */}
        <div className="w-full md:w-64 flex-shrink-0 bg-white rounded-xl shadow-sm border border-gray-100 p-4 overflow-y-auto" data-id="element-2180">
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3 px-2" data-id="element-2181">
            Folders
          </h3>
          <ul className="space-y-1" data-id="element-2182">
            {folders.map(folder => <li key={folder.id} data-id="element-2183">
                <button onClick={() => setActiveFolder(folder.id)} className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${activeFolder === folder.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`} data-id="element-2184">
                  <div className="flex items-center gap-2" data-id="element-2185">
                    <FolderIcon size={16} className={activeFolder === folder.id ? 'text-blue-500' : 'text-gray-400'} data-id="element-2186" />
                    {folder.name}
                  </div>
                  <span className={`text-xs ${activeFolder === folder.id ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2187">
                    {getFolderCount(folder.id)}
                  </span>
                </button>
              </li>)}
          </ul>

          <div className="mt-8" data-id="element-2188">
            <div onClick={() => setIsUploadModalOpen(true)} className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center hover:bg-gray-50 hover:border-blue-400 transition-colors cursor-pointer group" data-id="element-2189">
              <div className="w-10 h-10 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-blue-100" data-id="element-2190">
                <UploadCloudIcon size={20} className="text-blue-500" data-id="element-2191" />
              </div>
              <p className="text-sm font-medium text-gray-700" data-id="element-2192">
                Drag & Drop files
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-2193">or click to browse</p>
            </div>
          </div>
        </div>

        {/* Main Grid */}
        <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col min-h-0" data-id="element-2194">
          {/* Toolbar */}
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4 flex-shrink-0" data-id="element-2195">
            <h2 className="font-bold text-gray-800" data-id="element-2196">
              {folders.find(f => f.id === activeFolder)?.name}
            </h2>
            <div className="flex gap-3 w-full sm:w-auto" data-id="element-2197">
              <div className="relative w-full sm:w-64" data-id="element-2198">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} data-id="element-2199" />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search files..." className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2200" />
              </div>
              <button className="p-1.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50" data-id="element-2201">
                <FilterIcon size={16} data-id="element-2202" />
              </button>
            </div>
          </div>

          {/* Grid */}
          <div className="p-4 overflow-y-auto flex-1" data-id="element-2203">
            {filteredItems.length === 0 ? <div className="flex flex-col items-center justify-center h-full text-gray-500" data-id="element-2204">
                <ImageIcon size={48} className="text-gray-300 mb-4" data-id="element-2205" />
                <p data-id="element-2206">No media files found in this folder.</p>
              </div> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-2207">
                {filteredItems.map(item => <div key={item.id} className="group relative bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:border-blue-400 transition-colors" data-id="element-2208">
                    <div className="aspect-square bg-gray-200 relative overflow-hidden" data-id="element-2209">
                      <img src={item.url} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" data-id="element-2210" />

                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-blue-900/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2" data-id="element-2211">
                        <button onClick={() => {
                    setCurrentItem(item);
                    setIsPreviewModalOpen(true);
                  }} className="p-2 bg-white text-gray-700 rounded-full hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Preview" data-id="element-2212">
                          <EyeIcon size={16} data-id="element-2213" />
                        </button>
                        <button onClick={() => handleCopyUrl(item.url)} className="p-2 bg-white text-gray-700 rounded-full hover:text-blue-600 hover:bg-blue-50 transition-colors" title="Copy URL" data-id="element-2214">
                          <CopyIcon size={16} data-id="element-2215" />
                        </button>
                      </div>
                    </div>
                    <div className="p-3" data-id="element-2216">
                      <p className="text-xs font-medium text-gray-900 truncate" title={item.name} data-id="element-2217">
                        {item.name}
                      </p>
                      <div className="flex justify-between items-center mt-1" data-id="element-2218">
                        <p className="text-[10px] text-gray-500" data-id="element-2219">{item.size}</p>
                        <button onClick={() => {
                    setCurrentItem(item);
                    setIsDeleteModalOpen(true);
                  }} className="text-gray-400 hover:text-red-500 transition-colors" data-id="element-2220">
                          <TrashIcon size={12} data-id="element-2221" />
                        </button>
                      </div>
                    </div>
                  </div>)}
              </div>}
          </div>
        </div>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" data-id="element-2222">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-2223">
            <div className="flex items-center justify-between p-6 border-b border-gray-100" data-id="element-2224">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-2225">Upload Media</h2>
              <button onClick={() => setIsUploadModalOpen(false)} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-2226">
                <XIcon size={24} data-id="element-2227" />
              </button>
            </div>
            <div className="p-6" data-id="element-2228">
              <form id="upload-form" onSubmit={handleUploadSubmit} className="space-y-4" data-id="element-2229">
                <div data-id="element-2230">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2231">
                    Image URL *
                  </label>
                  <input type="url" required value={uploadUrl} onChange={e => setUploadUrl(e.target.value)} placeholder="https://example.com/image.jpg" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2232" />
                </div>
                <div data-id="element-2233">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2234">
                    File Name *
                  </label>
                  <input type="text" required value={uploadName} onChange={e => setUploadName(e.target.value)} placeholder="my-image.jpg" className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2235" />
                </div>
                <div data-id="element-2236">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2237">
                    Folder
                  </label>
                  <select value={uploadFolder} onChange={e => setUploadFolder(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none" data-id="element-2238">
                    {folders.filter(f => f.id !== 'all').map(f => <option key={f.id} value={f.id} data-id="element-2239">
                          {f.name}
                        </option>)}
                  </select>
                </div>
              </form>
            </div>
            <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end gap-3" data-id="element-2240">
              <button onClick={() => setIsUploadModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors" data-id="element-2241">
                Cancel
              </button>
              <button form="upload-form" type="submit" className="px-4 py-2 bg-blue-950 text-yellow-400 rounded-lg text-sm font-medium hover:bg-blue-900 transition-colors" data-id="element-2242">
                Upload File
              </button>
            </div>
          </div>
        </div>}

      {/* Delete Modal */}
      {isDeleteModalOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn" data-id="element-2243">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-2244">
            <div className="p-6 text-center" data-id="element-2245">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2246">
                <TrashIcon size={24} className="text-red-600" data-id="element-2247" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-id="element-2248">
                Delete File?
              </h2>
              <p className="text-gray-500 mb-6" data-id="element-2249">
                Are you sure you want to delete "{currentItem?.name}"? This
                action cannot be undone.
              </p>
              <div className="flex gap-3 justify-center" data-id="element-2250">
                <button onClick={() => setIsDeleteModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors flex-1" data-id="element-2251">
                  Cancel
                </button>
                <button onClick={handleDelete} className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex-1" data-id="element-2252">
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Preview Modal */}
      {isPreviewModalOpen && currentItem && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-fadeIn" onClick={() => setIsPreviewModalOpen(false)} data-id="element-2253">
          <button className="absolute top-6 right-6 text-white hover:text-gray-300 transition-colors" data-id="element-2254">
            <XIcon size={32} data-id="element-2255" />
          </button>
          <img src={currentItem.url} alt={currentItem.name} className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-2256" />
        </div>}
    </div>;
};
export default MediaManager;